import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global banner that surfaces LLM key budget-exhaustion so the whole
 * call-classification pipeline doesn't silently stall for a week (see
 * 2026-07-13 incident). Mounted at App root; polls every 60s while
 * authenticated.
 *
 * Behaviour:
 *   - Every signed-in user sees a red warning explaining the impact.
 *   - Master admins additionally see a "Mark Resolved" button that
 *     calls the clear endpoint after topping up.
 *   - Users can dismiss the banner for the current session via the X
 *     (server state stays; clear must be explicit master-admin action).
 */
const LlmBudgetBanner = () => {
  const { user, isAuthenticated } = useAuth();
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/admin/llm-health`);
        if (!cancelled) setHealth(r.data);
      } catch {
        // Silent — banner never blocks the page.
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;
  if (!health?.budget_exhausted) return null;
  if (dismissed) return null;

  const isMasterAdmin = user?.role === 'master_admin';

  const clearBanner = async () => {
    setClearing(true);
    try {
      await axios.post(`${API}/admin/llm-health/clear`);
      setHealth({ budget_exhausted: false });
    } catch {
      // no-op — button stays enabled
    } finally {
      setClearing(false);
    }
  };

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9999] bg-red-600 text-white shadow-lg"
      data-testid="llm-budget-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="flex-1 text-sm leading-snug">
          <p className="font-bold">
            LLM classification is paused — Universal Key budget exhausted
          </p>
          <p className="text-red-100 text-xs mt-0.5">
            New calls are still being ingested and transcribed, but deal-outcome, DISC and playbook classifiers are silently failing.
            {' '}Top up your Emergent Universal Key (Profile → Universal Key → Add Balance){isMasterAdmin ? ' and then click Mark Resolved.' : '. Ask your admin to top up.'}
            {health.last_error_at && (
              <span className="opacity-75 ml-1">First detected {new Date(health.last_error_at).toLocaleString()}.</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isMasterAdmin && (
            <button
              type="button"
              onClick={clearBanner}
              disabled={clearing}
              className="text-xs font-semibold px-3 py-1 rounded-md bg-white text-red-700 hover:bg-red-50 disabled:opacity-60 inline-flex items-center gap-1"
              data-testid="llm-budget-banner-clear"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              {clearing ? 'Clearing…' : 'Mark Resolved'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-red-100 hover:text-white p-1"
            aria-label="Dismiss"
            data-testid="llm-budget-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LlmBudgetBanner;
